import { useState } from "react";
import { Navigate, useLocation, useParams } from "react-router-dom";

import { ScreenLayout } from "@/app/components/layouts";
import {
  RefillRequirementsAlert,
  SymptomsForm,
} from "@/features/consultations/components";
import { useAvailableRefillRequests } from "@/features/my-meds/hooks";
import { medicationRequestSchema } from "@/features/my-meds/models";
import { MY_MEDS_ROUTE } from "@/features/my-meds/router";
import { LogoLoader } from "@/shared/components";
import { CATEGORY_SLUG } from "@/shared/domains";
import {
  FilterForm,
  RefillHealthieForm,
  RefillInjectionsForm,
  RefillRequestHeader,
} from "./sections";
import type { RefillFormStep } from "./utils";
import { getRefillFormHeaderDescription, REFILL_REQUEST_STEP } from "./utils";

export const RefillRequest = () => {
  const location = useLocation();
  const { productId } = useParams();

  const {
    data: availableRefillRequests,
    isLoading,
    isSuccess,
  } = useAvailableRefillRequests();

  const parsedMedicationRequestProduct =
    medicationRequestSchema.shape.product.safeParse(location.state);

  const medicationRequestProductById = availableRefillRequests?.find(
    (request) => request.product.id === Number(productId),
  )?.product;

  const medicationRequestProduct =
    medicationRequestProductById ?? parsedMedicationRequestProduct.data;

  const [step, setStep] = useState<RefillFormStep>(
    medicationRequestProduct?.category.slug !== CATEGORY_SLUG.WEIGHT_LOSS
      ? REFILL_REQUEST_STEP.HEALTHIE_FORM
      : REFILL_REQUEST_STEP.SYMPTOMS_FORM,
  );

  if (isSuccess && !medicationRequestProduct) {
    return <Navigate replace to={MY_MEDS_ROUTE.BASE} />;
  }

  return (
    <ScreenLayout className="gap-0">
      <LogoLoader show={isLoading} fullscreen />

      <RefillRequestHeader description={getRefillFormHeaderDescription(step)} />

      {step === REFILL_REQUEST_STEP.SYMPTOMS_FORM && (
        <SymptomsForm
          onSubmit={() => setStep(REFILL_REQUEST_STEP.INJECTIONS_FORM)}
        />
      )}

      {step === REFILL_REQUEST_STEP.INJECTIONS_FORM && (
        <RefillInjectionsForm
          onCancel={() => setStep(REFILL_REQUEST_STEP.SYMPTOMS_FORM)}
          onSubmit={() => setStep(REFILL_REQUEST_STEP.FILTER_FORM)}
        />
      )}

      {step === REFILL_REQUEST_STEP.FILTER_FORM && (
        <FilterForm
          onContinue={() => setStep(REFILL_REQUEST_STEP.HEALTHIE_FORM)}
        />
      )}

      {step === REFILL_REQUEST_STEP.HEALTHIE_FORM && (
        <div className="flex max-w-2xl flex-col gap-6">
          <RefillRequirementsAlert />

          {medicationRequestProduct?.id && (
            <RefillHealthieForm
              productId={medicationRequestProduct.id}
              onCancel={() => setStep(REFILL_REQUEST_STEP.FILTER_FORM)}
            />
          )}
        </div>
      )}
    </ScreenLayout>
  );
};
